import React, { useState, useEffect } from "react";
import {
  Switch,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "../styles/CookieConsent.css"; // Keep your existing CSS or modify it as needed

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: false,
    functional: false,
    performance: false,
    marketing: false,
  });

  const [expanded, setExpanded] = useState({
    necessary: false,
    functional: false,
    performance: false,
    marketing: false,
  });

  // Check if the cookie consent is already stored in localStorage
  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(
        localStorage.getItem("cookiePreferences")
      );
      if (savedPreferences) {
        setCookiePreferences(savedPreferences);
      }
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    localStorage.setItem(
      "cookiePreferences",
      JSON.stringify(cookiePreferences)
    );
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem("cookieConsent", "rejected");
    setShowBanner(false);
  };

  const handleSettings = () => {
    setShowSettings(true);
  };

  const closeSettingsModal = () => {
    setShowSettings(false);
  };

  // Handle changing individual cookie preference
  const handleCookieChange = (category) => {
    setCookiePreferences((prevState) => ({
      ...prevState,
      [category]: !prevState[category],
    }));
  };

  // Handle expanding/collapsing secondary text
  const toggleExpand = (category) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [category]: !prevExpanded[category],
    }));
  };

  return (
    <>
      {/* Cookie Consent Banner */}
      {showBanner && (
        <div className="cookie-banner">
          <div className="cookie-message">
            <p>
              We use cookies to improve your experience. By using our website,
              you agree to our use of cookies.
              <a href="#" onClick={handleSettings}>
                Learn more
              </a>
            </p>
          </div>
          <div className="cookie-actions">
            <button className="accept-btn" onClick={handleAccept}>
              Accept
            </button>
            <button className="reject-btn" onClick={handleReject}>
              Reject
            </button>
            <button className="settings-btn" onClick={handleSettings}>
              Cookie Settings
            </button>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal using MUI Dialog */}
      <Dialog
        open={showSettings}
        onClose={closeSettingsModal}
        sx={{
          "& .MuiDialog-paper": {
            minHeight: "300px", // Set a minimum height for the dialog
            maxHeight: "80vh", // Make sure the dialog is responsive and doesn't exceed 80% of the viewport height
            width: "400px", // Set a fixed width if needed
            borderRadius: "8px", // Optional: for rounded corners
          },
        }}
      >
        <DialogTitle
          sx={{ fontSize: "16px", fontWeight: "bold", textAlign: "center" }}
        >
          VERSIONED.IO
        </DialogTitle>

        <DialogContent
          sx={{ maxHeight: "400px", overflow: "auto", padding: "8px 16px" }}
        >
          <Typography
            variant="body2"
            paragraph
            sx={{ fontSize: "12px", lineHeight: 1.4 }}
          >
            Cookies and other similar technologies (“Cookies”) are important to
            the proper functioning of a site and to provide visitors with a
            seamless and customized experience.
          </Typography>

          <Typography
            variant="body2"
            paragraph
            sx={{ fontSize: "12px", lineHeight: 1.4 }}
          >
            Version uses Cookies to enable you to use our site. We also use
            cookies to enable you to personalize your use of our site, to
            provide you enhanced functionality and to continuously improve the
            performance of our site.
          </Typography>

          <Typography
            variant="body2"
            paragraph
            sx={{ fontSize: "12px", lineHeight: 1.4 }}
          >
            If you have Targeting cookies enabled below and depending on your
            account type or login state, we may allow third-party advertisers to
            show you advertising relevant to you on our website or products,
            using their Cookies on our site.
          </Typography>

          <Box mt={2}>
            <Typography
              variant="subtitle2"
              paragraph
              sx={{ fontSize: "13px", fontWeight: 500 }}
            >
              Types of Cookies We Use:
            </Typography>

            <List sx={{ padding: 0 }}>
              {/* Necessary Cookies */}
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <ListItem sx={{ padding: "8px 0" }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontSize: "12px" }}>
                        Necessary Cookies
                      </Typography>
                    }
                    secondary={
                      expanded.necessary && (
                        <Typography variant="body2" sx={{ fontSize: "10px" }}>
                          These cookies are required for basic site
                          functionality.
                        </Typography>
                      )
                    }
                  />
                  <Switch
                    checked={cookiePreferences.necessary}
                    onChange={() => handleCookieChange("necessary")}
                    color="primary"
                    sx={{ marginLeft: "auto" }}
                  />
                  <div
                    onClick={() => toggleExpand("necessary")}
                    style={{
                      cursor: "pointer",
                      marginLeft: "8px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {expanded.necessary ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </div>
                </ListItem>
              </Box>

              {/* Functional Cookies */}
              <ListItem sx={{ padding: "8px 0" }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>
                      Functional Cookies
                    </Typography>
                  }
                  secondary={
                    expanded.functional && (
                      <Typography variant="body2" sx={{ fontSize: "10px" }}>
                        These cookies allow us to provide enhanced features and
                        personalization.
                      </Typography>
                    )
                  }
                />
                <Switch
                  checked={cookiePreferences.functional}
                  onChange={() => handleCookieChange("functional")}
                  color="primary"
                  sx={{ marginLeft: "8px" }}
                />
                <div
                  onClick={() => toggleExpand("functional")}
                  style={{
                    cursor: "pointer",
                    marginLeft: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {expanded.functional ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </div>
              </ListItem>

              {/* Performance Cookies */}
              <ListItem sx={{ padding: "8px 0" }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>
                      Performance Cookies
                    </Typography>
                  }
                  secondary={
                    expanded.performance && (
                      <Typography variant="body2" sx={{ fontSize: "10px" }}>
                        These cookies help us improve the site performance by
                        gathering anonymous usage data.
                      </Typography>
                    )
                  }
                />
                <Switch
                  checked={cookiePreferences.performance}
                  onChange={() => handleCookieChange("performance")}
                  color="primary"
                  sx={{ marginLeft: "8px" }}
                />
                <div
                  onClick={() => toggleExpand("performance")}
                  style={{
                    cursor: "pointer",
                    marginLeft: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {expanded.performance ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </div>
              </ListItem>

              {/* Targeting Cookies */}
              <ListItem sx={{ padding: "8px 0" }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontSize: "12px" }}>
                      Targeting Cookies
                    </Typography>
                  }
                  secondary={
                    expanded.marketing && (
                      <Typography variant="body2" sx={{ fontSize: "10px" }}>
                        These cookies help display personalized ads based on
                        your interests.
                      </Typography>
                    )
                  }
                />
                <Switch
                  checked={cookiePreferences.marketing}
                  onChange={() => handleCookieChange("marketing")}
                  color="primary"
                  sx={{ marginLeft: "8px" }}
                />
                <div
                  onClick={() => toggleExpand("marketing")}
                  style={{
                    cursor: "pointer",
                    marginLeft: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {expanded.marketing ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </div>
              </ListItem>
            </List>
          </Box>
        </DialogContent>

        {/* Action buttons */}
        <DialogActions
          sx={{ padding: "8px 16px", justifyContent: "space-between" }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleAccept}
            sx={{ fontSize: "12px", padding: "6px 12px" }}
          >
            Save Preferences
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={closeSettingsModal}
            sx={{ fontSize: "12px", padding: "6px 12px" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
